import type {RouteObject} from '@github-ui/react-router'
import type React from 'react'

import type {ChildRoute, NavigatorRouteRegistration, PreloadableComponent} from './app-routing-types'

/**
 * Recursively collect all lazy components from routes that have a preload method.
 */
function collectAllPreloadableComponents(
  routes: Array<{Component?: React.ComponentType; children?: ChildRoute[]} | RouteObject>,
): PreloadableComponent[] {
  const components: PreloadableComponent[] = []

  for (const route of routes) {
    const component = route.Component as PreloadableComponent | undefined
    if (component?.preload) {
      components.push(component)
    }

    if ('children' in route && route.children) {
      components.push(...collectAllPreloadableComponents(route.children as typeof routes))
    }
  }

  return components
}

declare global {
  interface Window {
    scheduler?: {
      postTask: (callback: () => void, options: {priority: string}) => void
    }
  }
}

/**
 * Schedule a callback at background priority.
 * Prefers the native Scheduler API (Chrome 94+, Edge 94+, Firefox 128+) which
 * integrates with the browser's task prioritization. Falls back to
 * requestIdleCallback (polyfilled) for Safari and older browsers.
 */
function scheduleBackground(callback: () => void): void {
  // Prefer native Scheduler API (Chrome 94+, Edge 94+, Firefox 128+) for proper
  // browser-level task prioritization. Fall back to requestIdleCallback for Safari.
  if ('scheduler' in window && window.scheduler?.postTask) {
    void window.scheduler.postTask(callback, {priority: 'background'})
  } else {
    window.requestIdleCallback(callback, {timeout: 5000})
  }
}

/**
 * Preloads all lazy route components in the background with low priority.
 *
 * Processes components in small batches, awaiting each batch before scheduling
 * the next via requestIdleCallback. This limits concurrent network fetches to
 * `batchSize` at a time so preloading doesn't compete with user-initiated
 * requests. Individual preload failures are silently ignored via
 * Promise.allSettled so one broken chunk never blocks the rest.
 *
 * @param routes - The app's route configuration (Navigator or DataRouter routes)
 * @param batchSize - Number of components to preload per batch (default: 4)
 */
export function preloadAllRouteComponents(routes: NavigatorRouteRegistration[] | RouteObject[], batchSize = 4): void {
  // Only run on client side
  if (typeof window === 'undefined') return

  const components = collectAllPreloadableComponents(routes)
  if (components.length === 0) return

  let index = 0

  const preloadNextBatch = async () => {
    const batch = components.slice(index, index + batchSize)
    index += batchSize

    // Await this batch so we limit concurrent fetches to batchSize.
    // allSettled ensures one failure can't reject the group.
    await Promise.allSettled(batch.map(c => c.preload?.()))

    // Schedule next batch if there are more components
    if (index < components.length) {
      scheduleBackground(preloadNextBatch)
    }
  }

  // Schedule with low priority after initial render
  scheduleBackground(preloadNextBatch)
}
